import { isResource } from '@medplum/core'
import type {
  Coding,
  Library,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { codeableConcept, hasCodeableConcept } from '#src/codeableConcept.ts'
import {
  AI_SKILL_ACCESS_TAG_SYSTEM,
  LIBRARY_TOPIC,
  SKILL_EVIDENCE_FANOUT_EXTENSION_URL,
  SKILL_PREFERRED_MODEL_EXTENSION_URL,
} from '#src/codeSystems.ts'
import { Extension, Tag } from '#src/decorators.ts'
import { base64Decode, base64Encode } from '#src/encoding.ts'
import { ExtendedLibrary } from '#src/models/library.ts'
import { AI_SKILL_OWNER_TAG_SYSTEM } from '#src/models/skills/constants.ts'
import { isReferenceString } from '#src/references.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export const AiSkillScope = {
  Personal: 'personal',
  Shared: 'shared',
} as const

export type AiSkillScope = (typeof AiSkillScope)[keyof typeof AiSkillScope]

export class SkillTemplate extends ExtendedLibrary {
  @Tag({
    system: AI_SKILL_ACCESS_TAG_SYSTEM,
  })
  _skillAccess?: Coding

  /**
   * The preferred AI model name for this skill (e.g. 'anthropic', 'gemini').
   * When set, overrides the client-selected model for runs of this skill.
   * Must be a valid AIModelName; invalid values are ignored at runtime.
   */
  @Extension({
    url: SKILL_PREFERRED_MODEL_EXTENSION_URL,
    kind: 'valueString',
  })
  preferredModel?: string

  /* Which of the participant's signed records feed this skill's evidence
   * extraction: comma-separated `Role` codes, optionally prefixed with a source
   * kind — `notes:PCP`, `assessments:RN,PT`, or a bare `PCP`. Presence is the
   * opt-in; a skill with no value runs the ordinary single-agent loop, and a
   * value the reader cannot parse is treated the same way. */
  @Extension({
    url: SKILL_EVIDENCE_FANOUT_EXTENSION_URL,
    kind: 'valueString',
  })
  evidenceFanout?: string

  constructor(data?: ConstructorParameters<typeof ExtendedLibrary>[0]) {
    super({
      ...data,
      topic: [SkillTemplate.libraryTopic, ...(data?.topic ?? [])],
    })
  }

  get description(): string {
    return this.resource.description ?? ''
  }

  set description(description: string) {
    this.resource.description = description
  }

  get content(): string {
    return base64Decode(this.resource.content?.[0]?.data ?? '')
  }

  set content(content: string) {
    this.resource.content = [
      {
        title: 'SKILL.md',
        contentType: 'text/markdown',
        data: base64Encode(content),
      },
    ]
  }

  get scope(): AiSkillScope {
    // Fail closed: any code other than an explicit `shared` (missing,
    // corrupt, or unknown) resolves to Personal so authorization stays
    // restrictive. Do not loosen this default.
    return this._skillAccess?.code === AiSkillScope.Shared
      ? AiSkillScope.Shared
      : AiSkillScope.Personal
  }

  set scope(scope: AiSkillScope) {
    this._skillAccess = {
      system: AI_SKILL_ACCESS_TAG_SYSTEM,
      code: scope,
    }
  }

  get isShared(): boolean {
    return this.scope === AiSkillScope.Shared
  }

  protected get skillOwner(): Reference<Practitioner> | undefined {
    const owner = this.getTag(AI_SKILL_OWNER_TAG_SYSTEM)
    if (!owner) {
      return this.lastUpdatedByPractitioner
    }
    // A malformed owner tag must not fall back to the last editor.
    if (
      !(
        isReferenceString(owner.code) &&
        owner.code.startsWith('Practitioner/') &&
        owner.code.length <= 77
      )
    ) {
      return undefined
    }
    const ownerReference: Reference<Practitioner> = { reference: owner.code }
    if (owner.display) {
      ownerReference.display = owner.display
    }
    return ownerReference
  }

  canBeUsedBy(practitioner: Reference<Practitioner>): boolean {
    const owner = this.skillOwner
    return (
      this.isShared ||
      (owner?.reference != null && owner.reference === practitioner.reference)
    )
  }

  override formatFhirResource(resource: Library): Library {
    const content = resource.content?.[0]
    if (content) {
      resource.content = [
        {
          ...content,
          title: 'SKILL.md',
          contentType: 'text/markdown',
        },
      ]
    }

    return resource
  }

  static libraryTopic = codeableConcept({
    system: LIBRARY_TOPIC,
    code: 'ai-skill',
    display: 'AI Skill',
  })

  static isInstance(value: unknown): value is Library {
    return (
      isResource(value) &&
      isOfType(value, 'Library') &&
      hasCodeableConcept(value.topic, SkillTemplate.libraryTopic)
    )
  }

  static searchQuery(): QueryTypes {
    return {
      topic: this.libraryTopic,
      status: 'active',
      _sort: '-_lastUpdated',
      _count: '1000',
    }
  }
}
